import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Navbar from "../components/Navbar";
import {
  ArrowRightIcon,
  CheckCircleIcon,
  StarIcon,
  TrophyIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  SparklesIcon,
  HeartIcon,
  ShieldCheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';

const bgImages = [
  "/images/bg1.jpg",
  "/images/bg2.png",
  "/images/bg3.png",
  "/images/bg4.png",
  "/images/bg5.png",
  "/images/bg6.png",
  "/images/bg7.png",
  "/images/bg8.png",
  "/images/bg9.png",
  "/images/bg10.png"
];


const Home = () => {
  const [bgIndex, setBgIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const apply = () => setIsMobile(mq.matches);
    apply();
    mq.addEventListener ? mq.addEventListener('change', apply) : mq.addListener(apply);
    return () => {
      mq.removeEventListener ? mq.removeEventListener('change', apply) : mq.removeListener(apply);
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setBgIndex((prevIndex) => (prevIndex + 1) % bgImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const nextImage = () => {
    setBgIndex((prevIndex) => (prevIndex + 1) % bgImages.length);
  };

  const prevImage = () => {
    setBgIndex((prevIndex) => (prevIndex - 1 + bgImages.length) % bgImages.length);
  };

  return (
    <>
      <Navbar />
      <div className="text-gray-800">
        {/* Hero Section with Auto Slider */}
        <section
          className="min-h-[100svh] md:min-h-[100vh] flex flex-col justify-center items-center text-white text-center px-4 relative overflow-hidden"
        >
          {/* Background Slider with Pure Fade Effect */}
          <div className="absolute inset-0">
            {bgImages.map((image, index) => (
              <div
                key={index}
                className={`absolute inset-0 ${
                  index === bgIndex ? 'opacity-100' : 'opacity-0'
                }`}
                style={{
                  transition: 'opacity 1s ease-in-out',
                  transform: 'none',
                }}
              >
                <img
                  src={image}
                  alt={`Background ${index + 1}`}
                  loading={index === 0 ? 'eager' : 'lazy'}
                  onError={(e) => { e.currentTarget.src = '/images/bg1.jpg'; e.currentTarget.onerror = null; }}
                  className="w-full h-full object-cover object-center"
                  style={{
                    transform: 'none',
                    transition: 'none',
                    objectPosition: isMobile ? 'center 30%' : 'center'
                  }}
                />
                {/* Subtle Dark Overlay for Better Text Readability */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/20 to-transparent"></div>
              </div>
            ))}
          </div>

          {/* Hero Content */}
          <div className="relative z-10 max-w-4xl mx-auto">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6 leading-tight"
            >
              Building Champions,
              <span className="block bg-gradient-to-r from-gray-700 to-orange-700 bg-clip-text text-transparent">
                One Facility at a Time
              </span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-200 mb-6 md:mb-8 max-w-3xl mx-auto leading-relaxed px-4"
            >
              India's premier sports infrastructure company specializing in world-class facilities, 
              artificial turf, and professional sports flooring solutions.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            >
              <a
                href="/contact"
                className="inline-flex items-center justify-center gap-2 bg-green-800 text-white px-6 py-3 md:px-8 md:py-4 rounded-lg text-base md:text-lg font-semibold hover:bg-green-900 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 w-full sm:w-auto"
              >
                Get in Touch
                <ArrowRightIcon className="w-4 h-4 md:w-5 md:h-5" />
              </a>
              <a
                href="/projects"
                className="inline-flex items-center justify-center gap-2 bg-green-800 text-white px-6 py-3 md:px-8 md:py-4 rounded-lg text-base md:text-lg font-semibold hover:bg-green-900 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 w-full sm:w-auto"
              >
                View Projects
                <ArrowRightIcon className="w-4 h-4 md:w-5 md:h-5" />
              </a>
            </motion.div>
          </div>

          {/* Mobile Slider Controls */}
          <div className="md:hidden absolute bottom-8 left-1/2 transform -translate-x-1/2 flex items-center gap-4">
            <button
              onClick={prevImage}
              className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
            >
              <ChevronLeftIcon className="w-5 h-5 text-white" />
            </button>
            <button
              onClick={nextImage}
              className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
            >
              <ChevronRightIcon className="w-5 h-5 text-white" />
            </button>
          </div>

          {/* Mobile Slider Indicators */}
          <div className="md:hidden absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
            {bgImages.map((_, index) => (
              <button
                key={index}
                onClick={() => setBgIndex(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === bgIndex ? 'bg-white w-6' : 'bg-white/50'
                }`}
              />
            ))}
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,_rgba(0,0,0,0.05)_1px,_transparent_0)] bg-[size:20px_20px]"></div>
          <div className="max-w-7xl mx-auto px-4 relative">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              {[
                { number: "500+", label: "Projects Completed" },
                { number: "50+", label: "Cities Covered" },
                { number: "10+", label: "Years Experience" },
                { number: "100%", label: "Client Satisfaction" }
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="p-8 bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
                >
                  <div className="text-4xl font-bold bg-gradient-to-r from-gray-600 to-orange-600 bg-clip-text text-transparent mb-3">{stat.number}</div>
                  <div className="text-gray-600 font-medium">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* About Us */}
        <section className="py-16 md:py-20 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,_rgba(0,0,0,0.05)_1px,_transparent_0)] bg-[size:20px_20px]"></div>
          <div className="max-w-7xl mx-auto px-4 relative">
            <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="relative order-2 md:order-1"
              >
                <img
                  src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&h=600&fit=crop&crop=center"
                  alt="Sports Infrastructure Company"
                  className="rounded-2xl shadow-2xl w-full"
                />
                <div className="absolute -bottom-4 md:-bottom-6 -right-4 md:-right-6 bg-gradient-to-r from-gray-600 to-orange-600 p-4 md:p-6 rounded-xl shadow-xl text-white">
                  <div className="flex items-center gap-3 md:gap-4">
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <StarIcon key={i} className="w-4 h-4 md:w-5 md:h-5 text-yellow-400 fill-current" />
                      ))}
                    </div>
                    <div className="text-xs md:text-sm">
                      <div className="font-semibold">Trusted by 900+ Clients</div>
                      <div>Across India</div>
                    </div>
                  </div>
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="order-1 md:order-2"
              >
                <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4 md:mb-6">About Sports Experts Infra</h2>
                <p className="text-base md:text-lg text-gray-600 mb-6 md:mb-8 leading-relaxed">
                  Sports Experts Infra is India's premier sports infrastructure company, specializing in the design, construction, and maintenance of world-class sports facilities. With over 10 years of experience, we've transformed the sports landscape across the nation.
                </p>
                <div className="space-y-3 md:space-y-4">
                  {[
                    "ISO 2015:2025 Certified Company",
                    "FIFA & IAAF Approved Installations",
                    "State-of-the-art Equipment & Technology",
                    "Expert Team of Engineers & Technicians"
                  ].map((item, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span className="text-sm md:text-base text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Services Overview */}
        <section className="py-16 md:py-20 bg-gradient-to-br from-gray-50 to-orange-50 relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,_rgba(0,0,0,0.05)_1px,_transparent_0)] bg-[size:20px_20px]"></div>
          <div className="max-w-7xl mx-auto px-4 relative">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.15 }}
              viewport={{ once: true }}
              className="text-center mb-12 md:mb-16"
            >
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-4">Our Services</h2>
              <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
                Comprehensive sports infrastructure solutions tailored to meet international standards and exceed expectations
              </p>
            </motion.div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {[
                {
                  icon: <TrophyIcon className="w-8 h-8 text-white" />,
                  title: "Artificial Turf",
                  desc: "Durable, FIFA-approved artificial turf solutions, high-performance artificial turf suitable for Football, Box Cricket, Hockey, Volleyball, Cricket Net Practice, Badminton and more — installed with premium materials and expert craftsmanship.",
                  color: "from-emerald-500 to-teal-600",
                  iconBg: "from-emerald-600 to-teal-700"
                },
                {
                  icon: <BuildingOfficeIcon className="w-8 h-8 text-white" />,
                  title: "Acrylic Synthetic Courts",
                  desc: "Acrylic resin based synthetic sport flooring for training and competition of top quality product for Basketball, Tennis, Badminton, Pickleball, Volleyball, Running track, Skating and multi-sport facilities with advanced drainage systems.",
                  color: "from-blue-500 to-indigo-600",
                  iconBg: "from-blue-600 to-indigo-700"
                },
                {
                  icon: <SparklesIcon className="w-8 h-8 text-white" />,
                  title: "Indoor Wooden Floorings",
                  desc: "WSF Approved Free Floating Wooden Sports Flooring Systems Flooring Systems are designed and manufactured using imported timber which helps decrease impact on the knee, feet, shins and ankles. As per the medical studies, such flooring indudes lesser number of sports related injuries for Indoor Badminton, Indoor Squash, Indoor Basketball & more indoor sports like this.",
                  color: "from-amber-500 to-orange-600",
                  iconBg: "from-amber-600 to-orange-700"
                },
                {
                  icon: <SparklesIcon className="w-8 h-8 text-white" />,
                  title: "EPDM Flooring & Rubber Tracks",
                  desc: "EPDM (ethylene propylene diene monomer) flooring is a type of synthetic rubber flooring that is commonly used in a variety of applications, including playgrounds, walking tracks, sandwich & Full-Pour athelic track, gymnasiums, and other. It is known for its durability, flexibility, and resistance to heat, chemicals, and UV radiation.",
                  color: "from-purple-500 to-pink-600",
                  iconBg: "from-purple-600 to-pink-700"
                },
                {
                  icon: <SparklesIcon className="w-8 h-8 text-white" />,
                  title: "Indoor PVC Mat",
                  desc: "PVC mat is a safe, comfortable, and high-performance playing surface, providing excellent traction, shock absorption, and consistent ball bounce. These mats are engineered to reduce the risk of injuries and enhance player experience for both professional and recreational Badminton, Basketball, Pickleball & more indoor matting.",
                  color: "from-cyan-500 to-blue-600",
                  iconBg: "from-cyan-600 to-blue-700"
                },
                {
                  icon: <SparklesIcon className="w-8 h-8 text-white" />,
                  title: "Interlocking PP Tiles",
                  desc: "Interlocking PP tiles modular flooring system made from durable & flexible plastic. Making them easy to install, versatile, and suitable for various applications, including Basketball, Badminton, Pickleball, Children play area, outdoor gym, skating and other indoor & outdoor sports flooring.",
                  color: "from-rose-500 to-red-600",
                  iconBg: "from-rose-600 to-red-700"
                },
                {
                  icon: <SparklesIcon className="w-8 h-8 text-white" />,
                  title: "Padel Court",
                  desc: "A padel court is a rectangular, enclosed playing area, typically one-third the size of a tennis court, where padel, a racquet sport, is played. The court is divided by a net, and the walls are usually made of glass or metal mesh.",
                  color: "from-lime-500 to-green-600",
                  iconBg: "from-lime-600 to-green-700"
                }
              ].map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className={`bg-gradient-to-br ${service.color} p-6 md:p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 text-white`}
                >
                  <div className={`w-14 h-14 md:w-16 md:h-16 bg-gradient-to-br ${service.iconBg} rounded-xl flex items-center justify-center mb-4 md:mb-6 shadow-lg`}>
                    {service.icon}
                  </div>
                  <h3 className="text-lg md:text-xl font-semibold text-white mb-3">{service.title}</h3>
                  <p className="text-sm md:text-base text-white/90 leading-relaxed">{service.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-16 md:py-20 bg-gradient-to-br from-gray-500 to-gray-800 relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,_rgba(0,0,0,0.05)_1px,_transparent_0)] bg-[size:20px_20px]"></div>
          <div className="max-w-7xl mx-auto px-4 relative">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="text-center mb-12 md:mb-16"
            >
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">Why Choose Us</h2>
              <p className="text-base md:text-lg lg:text-xl text-gray-100 max-w-3xl mx-auto px-4">
                We combine expertise, innovation, and dedication to deliver exceptional sports infrastructure solutions
              </p>
            </motion.div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {[
                {
                  icon: <ShieldCheckIcon className="w-8 h-8 text-white" />,
                  title: "Quality Assured",
                  desc: "ISO 9001:2015 certified processes and materials",
                  color: "from-emerald-500 to-emerald-600",
                  iconBg: "from-emerald-600 to-emerald-700"
                },
                {
                  icon: <UserGroupIcon className="w-8 h-8 text-white" />,
                  title: "Expert Team",
                  desc: "Highly skilled professionals with years of experience",
                  color: "from-blue-500 to-blue-600",
                  iconBg: "from-blue-600 to-blue-700"
                },
                {
                  icon: <SparklesIcon className="w-8 h-8 text-white" />,
                  title: "Innovation",
                  desc: "Latest technology and best practices in the industry",
                  color: "from-purple-500 to-purple-600",
                  iconBg: "from-purple-600 to-purple-700"
                },
                {
                  icon: <HeartIcon className="w-8 h-8 text-white" />,
                  title: "Customer Focus",
                  desc: "Dedicated to exceeding client expectations",
                  color: "from-rose-500 to-rose-600",
                  iconBg: "from-rose-600 to-rose-700"
                }
              ].map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className={`bg-gradient-to-br ${feature.color} p-6 md:p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 text-white`}
                >
                  <div className={`w-14 h-14 md:w-16 md:h-16 bg-gradient-to-br ${feature.iconBg} rounded-xl flex items-center justify-center mb-4 md:mb-6 shadow-lg`}>
                    {feature.icon}
                  </div>
                  <h3 className="text-lg md:text-xl font-semibold text-white mb-3">{feature.title}</h3>
                  <p className="text-sm md:text-base text-white/90">{feature.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-20 bg-gradient-to-r from-gray-600 to-orange-600 text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,_rgba(255,255,255,0.1)_1px,_transparent_0)] bg-[size:20px_20px]"></div>
          <div className="max-w-7xl mx-auto px-4 relative">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">Ready to Transform Your Sports Facility?</h2>
              <p className="text-lg md:text-xl lg:text-2xl text-gray-100 mb-8 max-w-3xl mx-auto px-4">
                Let's discuss how we can help you create a world-class sports infrastructure that meets your needs and exceeds expectations.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/contact"
                  className="inline-flex items-center justify-center gap-2 bg-green-800 text-white px-6 py-3 md:px-8 md:py-4 rounded-lg text-base md:text-lg font-semibold hover:bg-green-900 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 w-full sm:w-auto"
                >
                  Get in Touch
                  <ArrowRightIcon className="w-4 h-4 md:w-5 md:h-5" />
                </a>
                <a
                  href="/projects"
                  className="inline-flex items-center justify-center gap-2 bg-green-800 text-white px-6 py-3 md:px-8 md:py-4 rounded-lg text-base md:text-lg font-semibold hover:bg-green-900 transition-all duration-300 hover:scale-105 w-full sm:w-auto"
                >
                  View Projects
                  <ArrowRightIcon className="w-4 h-4 md:w-5 md:h-5" />
                </a>
              </div>
            </motion.div>
          </div>
        </section>

      </div>
    </>
  );
};

export default Home;
